import { HrvIcon } from '@jouzen/ecom-components/icons/HrvIcon';
import { MeditationIcon } from '@jouzen/ecom-components/icons/MeditationIcon';
import { MoonIcon } from '@jouzen/ecom-components/icons/MoonIcon';
import { RestIcon } from '@jouzen/ecom-components/icons/RestIcon';
import { SunriseIcon } from '@jouzen/ecom-components/icons/SunriseIcon';
import { BREAKPOINTS } from '@jouzen/ecom-components/v3/consts/breakpoints';

import type { SecondaryAppUIProps } from '../_components/Timeline/SecondaryAppUI';
import type { SectionProps } from '../_components/Timeline/Section';
import { darkGradients, grayGradients, lightGradients } from './gradients';

export interface SectionData
  extends Omit<
    SectionProps,
    'activeIndex' | 'index' | 'previousIndex' | 'sectionCount'
  > {
  // App UI Lottie path displayed alongside the heart rate animation overlaying the video
  readonly secondaryAppUi: Omit<SecondaryAppUIProps, 'activeIndex' | 'index'>;
}

export const timelineSections: SectionData[] = [
  {
    altImage: {
      src: 'blue-sky/how-it-works/how_it_works_7am_d.png',
      alt: '',
      fill: true,
      sizes: '100vw',
    },
    appUi: {
      assets: [
        {
          id: '6am/readiness',
          lottieData: {
            fallbackImage: {
              alt: '',
              format: 'svg',
              src: '6am_readiness_app_ui',
            },
            height: 205,
            path: '6am/readiness',
            width: 300,
          },
        },
      ],
      backdrop: {
        variant: {
          sm: 'light',
          lg: 'light',
        },
      },
    },
    backgroundColor: 'sandstone-200',
    content: 'how_it_works_6am_copy',
    gradients: lightGradients,
    heading: 'how_it_works_6am_title',
    Icon: SunriseIcon,
    id: '6am',
    secondaryAppUi: {
      lottieData: {
        fallbackImage: {
          alt: '',
          format: 'svg',
          src: '6am_time_asleep_app_ui',
        },
        height: 144,
        path: '6am/time-asleep',
        width: 250,
      },
    },
    time: 'how_it_works_timeline_6am',
    variant: {
      sm: 'light',
      lg: 'dark',
    },
    videoSrcSet: [
      {
        src: 'blue-sky/how-it-works/how_it_works_7am_d.mp4',
        media: `(min-width: ${BREAKPOINTS.large}px)`,
      },
      {
        src: 'blue-sky/how-it-works/how_it_works_7am_m.mp4',
      },
    ],
  },
  {
    altImage: {
      src: 'blue-sky/how-it-works/how_it_works_12pm.png',
      alt: '',
      fill: true,
      sizes: '100vw',
    },
    appUi: {
      assets: [
        {
          id: '12pm/daily-scores-1',
          lottieData: {
            fallbackImage: {
              alt: '',
              format: 'svg',
              src: '12pm_daily_scores_top_app_ui',
            },
            height: 130,
            path: '12pm/daily-scores',
            width: 300,
          },
        },
        {
          id: '12pm/daily-scores-2',
          imageData: {
            alt: '',
            format: 'svg',
            height: 130,
            src: '12pm_daily_scores_bottom_app_ui',
            width: 300,
          },
        },
      ],
      backdrop: {
        anchor: {
          x: 'left',
          y: 'bottom',
        },
        size: {
          height: 130,
          width: 300,
        },
        variant: {
          sm: 'dark',
          lg: 'dark',
        },
      },
      wrapperClasses: 'lg:-translate-y-1/4',
    },
    backgroundColor: 'sandstone-200',
    content: 'how_it_works_12pm_copy',
    gradients: lightGradients,
    heading: 'how_it_works_12pm_title',
    Icon: HrvIcon,
    id: '12pm',
    secondaryAppUi: {
      imageData: {
        alt: '',
        format: 'png',
        height: 120,
        src: 'stretch_legs_notify_app_ui',
        width: 501,
      },
    },
    time: 'how_it_works_timeline_12pm',
    variant: {
      sm: 'light',
      lg: 'light',
    },
    videoSrcSet: [
      {
        src: 'blue-sky/how-it-works/how_it_works_12pm.mp4',
      },
    ],
  },
  {
    altImage: {
      src: 'blue-sky/how-it-works/how_it_works_3pm.png',
      alt: '',
      fill: true,
      sizes: '100vw',
    },
    appUi: {
      assets: [
        {
          id: '3pm/daytime-stress',
          lottieData: {
            fallbackImage: {
              alt: '',
              format: 'svg',
              src: '3pm_daytime_stress_app_ui',
            },
            height: 210,
            path: '3pm/daytime-stress',
            width: 300,
          },
        },
      ],
      backdrop: {
        variant: {
          sm: 'light',
          lg: 'dark',
        },
      },
    },
    backgroundColor: 'slate-100',
    content: 'how_it_works_3pm_copy',
    gradients: grayGradients,
    heading: 'how_it_works_3pm_title',
    Icon: MeditationIcon,
    id: '3pm',
    secondaryAppUi: {
      imageData: {
        alt: '',
        format: 'png',
        height: 400,
        src: 'meditate_app_ui',
        width: 500,
      },
    },
    time: 'how_it_works_timeline_3pm',
    variant: {
      sm: 'light',
      lg: 'light',
    },
    videoSrcSet: [
      {
        src: 'blue-sky/how-it-works/how_it_works_3pm.mp4',
      },
    ],
  },
  {
    altImage: {
      src: 'blue-sky/how-it-works/how_it_works_6pm.png',
      alt: '',
      fill: true,
      sizes: '100vw',
    },
    appUi: {
      assets: [
        {
          id: '6pm/tag-entry',
          lottieData: {
            dimensionsByLocale: {
              cs: {
                height: 210,
                width: 300,
              },
              da: {
                height: 210,
                width: 300,
              },
              de: {
                height: 250,
                width: 300,
              },
              en: {
                height: 210,
                width: 300,
              },
              es: {
                height: 269,
                width: 300,
              },
              fi: {
                height: 256,
                width: 300,
              },
              fr: {
                height: 250,
                width: 300,
              },
              it: {
                height: 250,
                width: 300,
              },
              ja: {
                height: 227,
                width: 300,
              },
              nb: {
                height: 210,
                width: 300,
              },
              nl: {
                height: 210,
                width: 300,
              },
              sv: {
                height: 210,
                width: 300,
              },
            },
            fallbackImage: {
              alt: '',
              format: 'svg',
              src: '6pm_tag_entry_app_ui',
            },
            path: '6pm/tag-entry',
          },
        },
      ],
      backdrop: {
        variant: {
          sm: 'light',
          lg: 'dark',
        },
      },
    },
    backgroundColor: 'slate-600',
    content: 'how_it_works_6pm_copy',
    gradients: darkGradients,
    heading: 'how_it_works_6pm_title',
    Icon: RestIcon,
    id: '6pm',
    secondaryAppUi: {
      imageData: {
        alt: '',
        format: 'svg',
        height: 336,
        src: '6pm_summary_app_ui',
        width: 250,
      },
    },
    time: 'how_it_works_timeline_6pm',
    variant: {
      sm: 'dark',
      lg: 'dark',
    },
    videoSrcSet: [
      {
        src: 'blue-sky/how-it-works/how_it_works_6pm.mp4',
      },
    ],
  },
  {
    altImage: {
      src: 'blue-sky/how-it-works/how_it_works_10pm_d.png',
      alt: '',
      fill: true,
      sizes: '100vw',
    },
    appUi: {
      assets: [
        {
          id: '10pm/bedtime',
          lottieData: {
            fallbackImage: {
              alt: '',
              format: 'svg',
              src: '10pm_bedtime_app_ui',
            },
            height: 299,
            path: '10pm/bedtime',
            width: 300,
          },
        },
      ],
      backdrop: {
        variant: {
          sm: 'light',
          lg: 'light',
        },
      },
    },
    backgroundColor: 'slate-600',
    content: 'how_it_works_10pm_copy',
    gradients: darkGradients,
    heading: 'how_it_works_10pm_title',
    Icon: MoonIcon,
    id: '10pm',
    secondaryAppUi: {
      imageData: {
        alt: '',
        format: 'png',
        height: 120,
        src: 'bedtime_approach_notify_app_ui',
        width: 500,
      },
    },
    time: 'how_it_works_timeline_10pm',
    variant: {
      sm: 'dark',
      lg: 'dark',
    },
    videoSrcSet: [
      {
        media: `(min-width: ${BREAKPOINTS.large}px)`,
        src: 'blue-sky/how-it-works/how_it_works_10pm_d.mp4',
      },
      {
        src: 'blue-sky/how-it-works/how_it_works_10pm_m.mp4',
      },
    ],
  },
];
